// Big lane so a single spin (≤45 steps) always lands well inside the
// rendered range. After each spin we snap-back to INITIAL_CENTER_INDEX
// invisibly — all tiles share the same image, so the user can never
// tell that the lane reset.
//
// MUST be odd so the lane has an exact centre tile under
// `justify-center`. With an even count the viewport centres on the
// gap BETWEEN two tiles, and every integer-step spin lands the same
// way → the wheel "stops between cards".
export const TILE_COUNT = 91;
export const INITIAL_CENTER_INDEX = 45;

export const MIN_SPIN_MS = 3400;
export const MAX_SPIN_MS = 5600;
// More steps than feels intuitive — real slots spin 3-5 full wheel
// revolutions before braking. Anything under ~20 steps reads as
// "carousel scroll", not "slot machine".
export const MIN_SPIN_STEPS = 28;
export const MAX_SPIN_STEPS = 44;

// Single smooth velocity curve for the main travel: rapid mid, gentle
// shoulders. No multi-segment easing — keyframe boundaries with
// different ease functions are exactly what was making the previous
// motion feel jerky.
export const SLOT_CURVE: [number, number, number, number] = [0.32, 0, 0.16, 1];
// Rebound after the wheel overshoots — short, gently-resolving curve
// for a mechanical "plonk" feel rather than a rubber-band bounce.
export const REBOUND_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
// Wheel overshoots its landing tile by this fraction of a tile-step,
// then snaps back. Classic mechanical-slot tell.
export const OVERSHOOT_FRACTION = 0.14;
// Fraction of total duration the rebound takes. Short = snappy
// "plonk"; long = bouncy.
export const REBOUND_T = 0.08;

// Settle pause after the wheel stops, before the neighbour-clear fade.
// Gives the eye a beat on the landed tile.
export const SETTLE_MS = 320;

// Motion blur peak while the wheel is at cruise speed. Higher values
// read as "faster spin" but blur the pack art beyond recognition.
export const BLUR_PEAK_PX = 2;

// "Unveil" phase — between Shuffle press and spin start.
export const UNVEIL_MS = 500;

// Neighbour-clear phase — after the wheel stops + settles, the two
// peeking neighbours fade their opacity to 0 so only the landed pack
// remains. Figma 735:53017.
export const CLEAR_TRANSITION_MS = 500;

// Pause between the cleared state and the reveal flash. Lets the eye
// settle on the lone pack for a beat before the glow takes over.
export const REVEAL_DELAY_MS = 1000;

// Crossfade window — landed pack fades out while the white glow
// square fades in. Same duration for both so they pivot at 50%.
export const REVEAL_FADE_MS = 500;

// Rotation speed of the sun-burst ray halo. Very slow, uniform
// linear motion — at full-screen scale anything quicker reads as
// flashing rather than gentle radiance.
export const RAYS_ROTATION_S = 60;
// Blur applied to the rotating rays so they read as soft beams of
// light, not crisp pie slices. The conic-gradient stops are wide
// enough that a heavy blur won't wash them away entirely.
export const RAYS_BLUR_PX = 24;

// "Expand" phase — after the glow square has appeared and the
// rarity tint has flashed, a blurred white plate scales up from
// pack-size to fill the screen. Acts as a wipe between ReadyStep
// and WinnerStep so the pack-to-card swap is hidden behind a beat
// of soft white. Long-ish delay so the rotating rays + tint have
// time to read before the wipe takes over; long expand duration
// (~1.5 s) so the swell feels gentle, not flashy.
export const EXPAND_DELAY_MS = 2000;
export const EXPAND_DURATION_MS = 1500;
// Scale factor that's guaranteed to overrun the viewport given the
// wipe plate's starting footprint (~210 px wide). 210 × 12 = 2520 px,
// plus the 32 px outer blur halo — covers any realistic iframe in
// both axes.
export const EXPAND_SCALE = 12;

// White-veil fade-out — once the wipe has filled the screen we mount
// WinnerStep behind it and fade it out so the card materialises in
// place of the white.
export const WHITE_FADE_MS = 500;

// Hover-tilt on the focal pack — subtle interactivity hint while the
// wheel is at rest. Reference: simey/hover-tilt + Pokemon-cards-css,
// but stripped down: no holographic / foil overlays, just a small
// rotate-towards-cursor + radial glare.
export const MAX_TILT_DEG = 7;
export const HOVER_SCALE = 1.02;
export const TILT_PERSPECTIVE_PX = 900;

// Per-distance brightness when the lane is at rest. Centre stays at
// 1; neighbours progressively darken so the focal pack reads as the
// hero (Figma 744:71985).
export function dimFor(distance: number): number {
  if (distance === 0) return 1;
  if (distance === 1) return 0.6;
  if (distance === 2) return 0.4;
  return 0.28;
}

export function randInt(min: number, max: number): number {
  return Math.floor(min + Math.random() * (max - min + 1));
}

export type AnimMode = 'idle' | 'unveil' | 'spin' | 'cleared' | 'reveal' | 'expand';
